import {
	DEFAULT_DIFFICULTY,
	DEFAULT_EXTRAS,
	DEFAULT_ROUND_SECONDS,
	DEFAULT_STREAK_MODE,
	DIFFICULTIES,
	type Difficulty,
	EXTRAS,
	type Extras,
	ROUND_SECONDS,
	type RoundSeconds,
	STREAK_MODES,
	type StreakMode,
} from "../sim";
import { MUSIC_IDS, MUSIC_VOLUME_IDS, type MusicId, type MusicVolume } from "./music";
import { SONG_IDS, type SongId } from "./songs";

/** What the menu remembers between visits (localStorage). */
export interface Settings {
	sound: boolean;
	seconds: RoundSeconds;
	difficulty: Difficulty;
	/** Minuspoäng: a miss costs a point. */
	missPenalty: boolean;
	/** Svit: hit streaks with a growing bonus, and the round's stats at the end. */
	streakMode: StreakMode;
	/** Bonus: which of the bats and ghosts come. */
	extras: Extras;
	/** Song mode: each collect plays the song's next note; null = the colours' plings. */
	song: SongId | null;
	/** Background music on a loop (music.ts); null = none. */
	music: MusicId | null;
	musicVolume: MusicVolume;
	/** A short buzz on a collect, where the device can (haptics.ts). */
	vibrate: boolean;
	/** Best score per kind of round (`bestKey`). */
	best: Record<string, number>;
	/** The next round is Mardröm; the other round choices then don't apply. */
	nightmare: boolean;
	/** Mardröm's list: this device's best runs by time and by score (`recordNightmare`). */
	nightmareScores: NightmareScore[];
}

export interface NightmareScore {
	score: number;
	/** Time alive. */
	ms: number;
	/** When it was played (ISO 8601). */
	date: string;
}

export type NightmareSort = "time" | "score";
/** Runs kept and shown per sort order. */
export const NIGHTMARE_LIST_SIZE = 10;

const KEY = "halloween.settings.v1";

/**
 * Rounds are only compared with rounds of the same kind. A Normal round without Minuspoäng and
 * Svit, with both bonus visitors, keeps the plain round length as its key, so records from before the options still count.
 */
export function bestKey(
	seconds: RoundSeconds,
	difficulty: Difficulty,
	missPenalty: boolean,
	streakMode: StreakMode = DEFAULT_STREAK_MODE,
	extras: Extras = DEFAULT_EXTRAS,
) {
	const kind = [
		difficulty === DEFAULT_DIFFICULTY ? "" : difficulty,
		missPenalty ? "minus" : "",
		streakMode === DEFAULT_STREAK_MODE ? "" : streakMode,
		extras === DEFAULT_EXTRAS ? "" : extras,
	];
	return [String(seconds), ...kind.filter(Boolean)].join("-");
}

const others = <T>(all: readonly T[], skip: T) => all.filter((v) => v !== skip).join("|");
const BEST_KEY = new RegExp(
	`^(${ROUND_SECONDS.join("|")})(-(${others(DIFFICULTIES, DEFAULT_DIFFICULTY)}))?(-minus)?(-(${others(STREAK_MODES, DEFAULT_STREAK_MODE)}))?(-(${others(EXTRAS, DEFAULT_EXTRAS)}))?$`,
);

export function defaultSettings(): Settings {
	return {
		sound: true,
		seconds: DEFAULT_ROUND_SECONDS,
		difficulty: DEFAULT_DIFFICULTY,
		missPenalty: false,
		streakMode: DEFAULT_STREAK_MODE,
		extras: DEFAULT_EXTRAS,
		song: null,
		music: null,
		musicVolume: "mid",
		vibrate: true,
		best: {},
		nightmare: false,
		nightmareScores: [],
	};
}

/** Validates stored data field by field; anything unusable falls back to the default. */
export function parseSettings(raw: unknown): Settings {
	const s = defaultSettings();
	if (typeof raw !== "object" || raw === null) return s;
	const r = raw as Record<string, unknown>;
	if (typeof r.sound === "boolean") s.sound = r.sound;
	if (ROUND_SECONDS.includes(r.seconds as RoundSeconds)) s.seconds = r.seconds as RoundSeconds;
	if (DIFFICULTIES.includes(r.difficulty as Difficulty)) s.difficulty = r.difficulty as Difficulty;
	if (typeof r.missPenalty === "boolean") s.missPenalty = r.missPenalty;
	if (STREAK_MODES.includes(r.streakMode as StreakMode)) s.streakMode = r.streakMode as StreakMode;
	if (EXTRAS.includes(r.extras as Extras)) s.extras = r.extras as Extras;
	if (SONG_IDS.includes(r.song as SongId)) s.song = r.song as SongId;
	if (MUSIC_IDS.includes(r.music as MusicId)) s.music = r.music as MusicId;
	if (MUSIC_VOLUME_IDS.includes(r.musicVolume as MusicVolume)) {
		s.musicVolume = r.musicVolume as MusicVolume;
	}
	if (typeof r.vibrate === "boolean") s.vibrate = r.vibrate;
	if (typeof r.nightmare === "boolean") s.nightmare = r.nightmare;
	if (typeof r.best === "object" && r.best !== null) {
		for (const [key, v] of Object.entries(r.best as Record<string, unknown>)) {
			if (BEST_KEY.test(key) && typeof v === "number" && Number.isInteger(v) && v > 0) {
				s.best[key] = v;
			}
		}
	}
	if (Array.isArray(r.nightmareScores)) {
		s.nightmareScores = r.nightmareScores.filter(isNightmareScore);
	}
	return s;
}

function isNightmareScore(raw: unknown): raw is NightmareScore {
	const r = raw as Partial<NightmareScore> | null;
	return (
		typeof r === "object" &&
		r !== null &&
		Number.isInteger(r.score) &&
		(r.score ?? -1) >= 0 &&
		Number.isInteger(r.ms) &&
		(r.ms ?? -1) >= 0 &&
		typeof r.date === "string" &&
		!Number.isNaN(Date.parse(r.date))
	);
}

export function loadSettings(): Settings {
	try {
		return parseSettings(JSON.parse(localStorage.getItem(KEY) ?? "null"));
	} catch {
		return defaultSettings();
	}
}

export function saveSettings(settings: Settings): void {
	try {
		localStorage.setItem(KEY, JSON.stringify(settings));
	} catch {
		// Private mode or storage full: the game still works, it just forgets.
	}
}

/** The best score for the round the menu has chosen (0 = none yet). */
export function bestFor(settings: Settings): number {
	return settings.best[keyOf(settings)] ?? 0;
}

/** Records a finished round's score for the chosen kind; true when it beats the previous best. */
export function recordScore(settings: Settings, score: number): boolean {
	const key = keyOf(settings);
	if (score <= (settings.best[key] ?? 0)) return false;
	settings.best[key] = score;
	return true;
}

/** Mardröm's list in one sort order, best first; the other number breaks ties. */
export function nightmareTop(
	scores: readonly NightmareScore[],
	sort: NightmareSort,
): NightmareScore[] {
	return [...scores]
		.sort((a, b) =>
			sort === "time" ? b.ms - a.ms || b.score - a.score : b.score - a.score || b.ms - a.ms,
		)
		.slice(0, NIGHTMARE_LIST_SIZE);
}

/** Adds a run, keeping the top of both sort orders; says which records it beat. */
export function recordNightmare(
	settings: Settings,
	run: NightmareScore,
): { newTime: boolean; newScore: boolean; bestMs: number; bestScore: number } {
	const prev = settings.nightmareScores;
	const bestMs = Math.max(0, ...prev.map((r) => r.ms));
	const bestScore = Math.max(0, ...prev.map((r) => r.score));
	const all = [...prev, run];
	const byTime = nightmareTop(all, "time");
	const byScore = nightmareTop(all, "score");
	settings.nightmareScores = all.filter((r) => byTime.includes(r) || byScore.includes(r));
	return {
		newTime: run.ms > bestMs,
		newScore: run.score > bestScore,
		bestMs: Math.max(bestMs, run.ms),
		bestScore: Math.max(bestScore, run.score),
	};
}

function keyOf(s: Settings): string {
	return bestKey(s.seconds, s.difficulty, s.missPenalty, s.streakMode, s.extras);
}
